import * as React from "react";
import {useForm, Controller} from "react-hook-form";
import {useState} from "react";
import {CustomerFactory} from "../../typescript-library";
import {getShopId} from "../lib/getShopId";
import Checkbox from "../lib/form/Checkbox/Checkbox";
import {Trans, useTranslation} from "react-i18next";
import styles from "./NewsletterRegistrationPage.module.scss";
import {emailRegExp, mailCheckRun} from "../../helpers/emailRegExp";
import NewsletterBenefits from "../customer/account/newsletter/NewsletterBenefits";
import Spinner from "../utils/loader/Spinner";
import {RegistrationVoucher} from "../../helpers/registrationVoucher";
import {cdnDomain} from "../../helpers/cnxDomains";

const NewsletterRegistrationPage: React.FC = () => {
  const {t} = useTranslation();
  const {
    register,
    watch,
    handleSubmit,
    formState: {errors},
    control,
  } = useForm<{email: string; consent: boolean}>({mode: "onSubmit", defaultValues: {email: "", consent: false}});

  const [customerEmail, setCustomerEmail] = useState("");
  const [successState, setSuccessState] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const resendEmail = () => setSuccessState(false);

  const TransBlock: React.FC = () => {
    if (!RegistrationVoucher.enabled) {
      return t("newsletter.registrationDiscountShortText");
    }

    return (
      <Trans
        i18nKey="newsletter.callToNewsletterAndGetCoupon--amountFormatted"
        ns="translations"
        values={{amountFormatted: RegistrationVoucher.amountFormatted}}
        components={{
          b: <b />,
          s: <sup />,
        }}
      />
    );
  };

  const onSubmit = handleSubmit((data) => {
    setIsLoading(true);
    void CustomerFactory.createOrUpdateCustomer(getShopId(), {email: data.email, newsletterFlag: true})
      .then(() => {
        // TODO show frame saying 'please check your email inbox'
        setCustomerEmail(data.email);
        setSuccessState(true);
      })
      .catch(() => {
        setSuccessState(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  });

  const Image = () => {
    // url to image, without file extension!
    const src = `${
      cdnDomain[getShopId()]
    }/media/_Banner_Newsletter-Registrierung/Banner-Hero-Newsletteranmeldung-winter2024`;

    return (
      <picture>
        <source type="image/webp" srcSet={`${src}.webp`} />
        <source type="image/jpg" srcSet={`${src}.jpg`} />
        <img src={`${src}.jpg`} alt={t("newsletter.neverMissAnyDiscounts")} />
      </picture>
    );
  };

  return (
    <div>
      <div className={styles.banner}>
        <div className={styles.bannerContent}>
          <div className={styles.bannerContentBox}>
            <h2 className={styles.bannerContentTitle}>Newsletter</h2>
            <p className={styles.desktop}>
              <TransBlock />
            </p>
          </div>
          <Image />
        </div>
        <div className={styles.mobile}>
          <TransBlock />
        </div>
      </div>
      {successState ? (
        <div className={styles.form}>
          <h2>{t("newsletter.almostThere")}</h2>
          <p>
            <Trans
              i18nKey="newsletter.confirmRegistration--customerEmail"
              ns="translations"
              values={{customerEmail}}
              components={{
                1: <b />,
              }}
            />
          </p>
          <p>
            <Trans
              i18nKey="newsletter.resendMail"
              ns="translations"
              components={{
                1: <span className={styles.resendLink} onClick={resendEmail} />,
              }}
            />
          </p>
        </div>
      ) : (
        <form className={styles.form} onSubmit={(evt: React.FormEvent<HTMLFormElement>) => void onSubmit(evt)}>
          <p className={styles.emailLabel}>{t("form.yourEmailAddress")}</p>
          <span className={styles.invalidInputMessage}>{errors.email && errors.email.message}</span>
          <input
            type="email"
            className={styles.input}
            {...register("email", {
              required: {
                value: true,
                message: t("form.pleaseComplete"),
              },
              pattern: {
                value: emailRegExp,
                message: t("form.invalidEmailAddress"),
              },
              validate: (val) => mailCheckRun(val) || t("form.invalidEmailAddress"),
            })}
          />
          <div>
            <Controller
              control={control}
              render={({field}) => (
                <Checkbox
                  className={errors.consent ? styles.checkboxError : styles.checkbox}
                  color={"green"}
                  checked={watch().consent}
                  onChange={(checked) => {
                    field.onChange(checked);
                  }}
                >
                  <span>
                    <Trans
                      i18nKey="form.iAgree"
                      ns="translations"
                      components={{
                        a: <a href={t("url.privacyLink")} target="_blank" rel={"noreferrer"} />,
                      }}
                    />
                  </span>
                </Checkbox>
              )}
              rules={{required: true}}
              name={"consent"}
            />
            {errors.consent && <span className={styles.mandatory}>{t("form.confirmPrivacyPolicy")}</span>}
          </div>
          <button formAction="submit" className={styles.submitBtn}>
            {isLoading ? <Spinner /> : <>{t("newsletter.subscribeForFree")}</>}
          </button>
        </form>
      )}

      <NewsletterBenefits />

      <div>
        <h3>{t("newsletter.alreadySubscribed")}</h3>
        <p>
          <Trans
            i18nKey="newsletter.editSettingsInCustomerAccount"
            ns="translations"
            components={{
              a: <a href={`${t("url.userAccountLink")}?view=subscriptions`} target="_blank" rel="noreferrer" />,
            }}
          />
        </p>
      </div>
    </div>
  );
};

export default NewsletterRegistrationPage;
